import { useCallback, ChangeEvent, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import {
  Autocomplete,
  Button,
  Icons,
  Switch,
  useDisruptiveActionDialog,
  Typography,
  Stack,
} from '@cast/design-system';
import { Cluster } from '@cast/types';

import { pageTourSteps } from 'features/demo/tour-steps';
import { TourSteps } from 'features/demo/types';
import {
  useDeleteAllFixturesMutation,
  useDeleteStepFixturesMutation,
  useSaveRecordedDataMutation,
} from 'hooks/mutations/dev-tools';
import { useNavigate } from '@cast/infrastructure-organization';

import { useOpTourRecorderState } from './op-tour-recorder-state';

import { useClustersQuery } from '@cast/shared-cluster-context-data-access';

const tourStepOptions = Object.values(TourSteps);

const fixturesGroup = 'op-demo';
const PLACEHOLDER_CLUSTER_ID = '11111111-1111-1111-1111-111111111111';
const PLACEHOLDER_REBALANCE_ID = 'd6a87a19-51c8-4934-8c93-192a3c3d00cb';

const getUrlForTourStep = (
  tourStep: string,
  clusterId: string
): string | null => {
  for (const page of pageTourSteps) {
    const found = page.steps.some((step) =>
      typeof step === 'string' ? step === tourStep : step.id === tourStep
    );
    if (found) {
      return page.url
        .replace(new RegExp(PLACEHOLDER_CLUSTER_ID, 'g'), clusterId)
        .replace(new RegExp(PLACEHOLDER_REBALANCE_ID, 'g'), clusterId);
    }
  }
  return null;
};

const getNextTourStep = (currentStep: string): string | null => {
  const currentIndex = tourStepOptions.indexOf(currentStep as TourSteps);
  if (currentIndex === -1 || currentIndex >= tourStepOptions.length - 1) {
    return null;
  }
  return tourStepOptions[currentIndex + 1];
};

export const OpTourRecorder = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const { clusters } = useClustersQuery();

  const isActive = useOpTourRecorderState((state) => state.isActive);
  const tourStep = useOpTourRecorderState((state) => state.tourStep);
  const clusterId = useOpTourRecorderState((state) => state.clusterId);
  const recordedData = useOpTourRecorderState((state) => state.recordedData);
  const setIsActive = useOpTourRecorderState((state) => state.setIsActive);
  const setTourStep = useOpTourRecorderState((state) => state.setTourStep);
  const setClusterId = useOpTourRecorderState((state) => state.setClusterId);
  const clearRecordedData = useOpTourRecorderState(
    (state) => state.clearRecordedData
  );

  const { mutate: saveRecordedData, isPending: isSaving } =
    useSaveRecordedDataMutation();
  const { mutate: deleteStepFixtures, isPending: isDeletingStep } =
    useDeleteStepFixturesMutation();
  const { mutate: deleteAllFixtures, isPending: isDeletingAll } =
    useDeleteAllFixturesMutation();

  const { openDisruptiveActionDialog } = useDisruptiveActionDialog();

  const selectedCluster = useMemo(
    () => clusters?.find((c) => c.id === clusterId) ?? null,
    [clusters, clusterId]
  );

  const nextStep = useMemo(
    () => (tourStep ? getNextTourStep(tourStep) : null),
    [tourStep]
  );

  const handleClusterChange = useCallback(
    (_option: Cluster, value: string) => {
      setClusterId(value);
      // Clear tour step when cluster changes to avoid URL mismatch
      setTourStep('');
      clearRecordedData();
    },
    [setClusterId, setTourStep, clearRecordedData]
  );

  const navigateToStep = useCallback(
    (step: string) => {
      if (!clusterId) return;

      const targetUrl = getUrlForTourStep(step, clusterId);
      if (targetUrl && targetUrl !== location.pathname) {
        navigate(targetUrl);
      }
      queryClient.invalidateQueries();
    },
    [clusterId, location.pathname, navigate, queryClient]
  );

  const handleTourStepChange = useCallback(
    (value: string | null) => {
      if (value) {
        setTourStep(value);
        clearRecordedData();
        navigateToStep(value);
      }
    },
    [setTourStep, clearRecordedData, navigateToStep]
  );

  const handleNextStep = useCallback(() => {
    if (nextStep) {
      handleTourStepChange(nextStep);
    }
  }, [nextStep, handleTourStepChange]);

  const handleToggle = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setIsActive(checked);
      if (checked) {
        queryClient.invalidateQueries();
      }
    },
    [setIsActive, queryClient]
  );

  const handleSave = useCallback(() => {
    if (!tourStep || Object.keys(recordedData).length === 0) {
      return;
    }
    saveRecordedData({ fixturesGroup, tourStep, data: recordedData });
  }, [tourStep, recordedData, saveRecordedData]);

  const handleDeleteStaticFixtures = useCallback(() => {
    if (!tourStep) return;

    openDisruptiveActionDialog({
      title: `Delete fixtures for step "${tourStep}"?`,
      onProceed: () => {
        deleteStepFixtures({ fixturesGroup, tourStep });
      },
    });
  }, [deleteStepFixtures, openDisruptiveActionDialog, tourStep]);

  const handleDeleteAllStaticFixtures = useCallback(() => {
    openDisruptiveActionDialog({
      title: 'Delete fixtures for ALL steps?',
      onProceed: () => {
        deleteAllFixtures({ fixturesGroup });
      },
    });
  }, [deleteAllFixtures, openDisruptiveActionDialog]);

  return (
    <Stack height="100%" py={8} gap={16}>
      <Stack direction="row" alignItems="center" gap={12}>
        <Typography variant="P12M" sx={{ minWidth: 100 }}>
          Cluster:
        </Typography>
        <Autocomplete<Cluster, string>
          options={clusters ?? []}
          optionLabel={(c) => c?.name}
          optionValue={(c) => c?.id}
          value={selectedCluster ? clusterId : undefined}
          onChange={handleClusterChange}
          inputText="Select cluster..."
          size="sm"
          sx={{ minWidth: 250 }}
          testId="recorder-cluster-autocomplete"
        />
      </Stack>

      <Stack direction="row" alignItems="center" gap={12}>
        <Typography variant="P12M" sx={{ minWidth: 100 }}>
          Recording:
        </Typography>
        <Switch
          checked={isActive}
          onChange={handleToggle}
          size="sm"
          testId="recorder-active-switch"
        />
        <Typography variant="P12R" color={isActive ? 'green.500' : 'grey.500'}>
          {isActive ? 'Active' : 'Inactive'}
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" gap={12}>
        <Typography variant="P12M" sx={{ minWidth: 100 }}>
          Tour Step:
        </Typography>
        <Autocomplete
          options={tourStepOptions}
          value={tourStep || null}
          onChange={handleTourStepChange}
          inputText="Select tour step..."
          size="sm"
          sx={{ minWidth: 250 }}
          testId="recorder-tour-step-autocomplete"
          applySortingWhileSearching={false}
          disabled={!clusterId}
        />
        <Button
          onClick={handleNextStep}
          disabled={!nextStep || !clusterId}
          size="sm"
          testId="recorder-next-step-button"
        >
          <Icons.ArrowRight size={16} />
        </Button>
        <Button
          onClick={handleDeleteStaticFixtures}
          disabled={!tourStep || !clusterId || isDeletingStep}
          variant="danger"
          size="sm"
          testId="recorder-delete-step-fixtures-button"
        >
          <Icons.X size={16} />
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" gap={12}>
        <Typography variant="P12M" sx={{ minWidth: 100 }}>
          Actions:
        </Typography>
        <Button
          onClick={handleSave}
          disabled={!tourStep || !isActive || isSaving}
          size="sm"
          testId="recorder-save-button"
        >
          Save Recorded Data
        </Button>
        <Button
          onClick={handleDeleteAllStaticFixtures}
          disabled={isDeletingAll}
          variant="danger"
          size="sm"
          testId="recorder-delete-all-static-button"
        >
          Delete All Fixtures
        </Button>
      </Stack>
    </Stack>
  );
};
